import { deriveMatchMinute } from '../domain/clock';
import { isInMatch, MATCH_STATE } from '../domain/machine';
import { read, TABLE, write } from '../data/db';
import { matches, session } from '../data/repos';
import type { MatchRow } from '../types';
import { log } from './logger';

/**
 * SES-3 / design §2.5 — relaunch lands on the correct screen, with the clock reconstructed.
 *
 * > On app launch, a session in any non-`archived` state resumes directly into its correct screen
 * > and capture phase, with the clock reconstructed from persisted anchors. Survives process kill
 * > and full browser restart.
 *
 * ── Two rows, and neither is a statechart ────────────────────────────────────
 *
 * Design §1.4 rejects a persisted statechart snapshot outright: session configuration is derived
 * from the `Match` row plus a tiny `UiSession` record, *"so a UI refactor is never a Dexie
 * migration."* Launch therefore reads exactly two singletons:
 *
 * - **`activeMatch`** — the resume *pointer*, present iff a match is in `preMatch` or an in-match
 *   state. It is authoritative.
 * - **`UiSession`** — `{matchId, surface, surfaceParams}`, written on surface **entry and exit
 *   only, never on the tap path**. It is a *hint*: it says which screen, not which match.
 *
 * ── Why the clock needs no reconstruction step ───────────────────────────────
 *
 * There is nothing to rebuild. The four anchors live on the `Match` row and a `MatchRow`
 * structurally satisfies `MatchAnchors`, so the resumed minute is `deriveMatchMinute(match,
 * match.state, now)` — the same total function that produced every stored minute (INV-7, INV-10).
 * A match killed at 62′ and relaunched two minutes later reads 64′ because the anchor never moved,
 * which is exactly why SES-4 can say *"the display clock is best-effort; correctness never depends
 * on timers firing."*
 *
 * ── Why `UiSession` is only ever a hint ──────────────────────────────────────
 *
 * The two rows are written by different commits — the pointer moves inside a transition's
 * transaction, the surface on a navigation — so they can disagree after a kill in between. Trusting
 * `UiSession.matchId` would resume into a screen belonging to a match that is no longer active. The
 * pointer decides *which* match; the surface hint applies only when it names the same one, and
 * otherwise the state's own default surface is used.
 */

const MODULE = 'resume';

/**
 * The surfaces §2.5 enumerates. Strings rather than a `types/` enum: `UiSessionRow.surface` is
 * typed `string` precisely so adding a screen is not a schema change (§1.4), and the later units
 * that own these screens will add their own without touching `data/`.
 */
export const SURFACE = {
  home: 'home',
  /**
   * The Pre-Match screen. Named `setup` and **not** `preMatch` on purpose: the SES-1 lint rule
   * refuses a `MatchState` literal in `app/`, and it fired on this very line during U-4's first
   * lint run. That is the rule working — a surface name and a state name that spell the same string
   * are exactly how a hand-rolled state grouping starts. §2.2 already calls this thing "an unstarted
   * setup", so the vocabulary was available and the collision was avoidable.
   */
  setup: 'setup',
  board: 'board',
  discovery: 'discovery',
  halftimeHub: 'halftime.hub',
} as const;

export type ResumeTarget =
  | {
      readonly kind: 'match';
      readonly match: MatchRow;
      readonly surface: string;
      readonly surfaceParams: Record<string, unknown>;
    }
  /** SES-11 — Home, showing the pending-distillation queue. */
  | { readonly kind: 'home' };

/**
 * Resolves what launch should show. One readonly transaction, so the two singletons and the match
 * row come from a single consistent snapshot rather than three independently-timed reads.
 *
 * @throws {StorageError} if the rows cannot be read. Never swallowed: a launch that silently fell
 *   back to Home would look identical to "no match in progress", and the user would discover the
 *   difference by finding their first half missing.
 */
export async function resolveResume(): Promise<ResumeTarget> {
  return read(
    [TABLE.activeMatch, TABLE.uiSession, TABLE.matches],
    async (): Promise<ResumeTarget> => {
      const pointer = await session.readActiveMatch();
      if (pointer?.matchId == null) return { kind: 'home' };

      const match = await matches.getMatch(pointer.matchId);
      if (match === undefined) {
        // A cascade removed the match but the pointer survived — reachable if a future path deletes
        // outside INV-8's transaction. Reported rather than silently swallowed: it means the
        // cascade is not atomic, which is a defect in something else entirely.
        log.warn(MODULE, 'the resume pointer names a match that no longer exists', {
          matchId: pointer.matchId,
        });
        return { kind: 'home' };
      }

      // §2.5: the pointer is present iff a match is in `preMatch` or an in-match state. A `fullTime`
      // match must not hijack launch — SES-11 says any number may sit there indefinitely, and Home's
      // pending-distillation queue is where they belong (§19 F-13).
      if (!isResumable(match)) {
        log.warn(MODULE, 'the resume pointer names a match that is no longer active', {
          matchId: match.id,
          state: match.state,
        });
        return { kind: 'home' };
      }

      const hint = await session.readUiSession();
      const usable = hint !== undefined && hint.matchId === match.id;
      return {
        kind: 'match',
        match,
        surface: usable ? hint.surface : defaultSurfaceFor(match),
        surfaceParams: usable ? hint.surfaceParams : {},
      };
    },
    'resolveResume',
  );
}

/** §2.5 — `activeMatch` is present iff the match is in `preMatch` or an in-match state. */
export function isResumable(match: MatchRow): boolean {
  return match.state === MATCH_STATE.preMatch || isInMatch(match.state);
}

/**
 * Where a resumable match belongs when there is no usable surface hint.
 *
 * SES-3 requires the correct screen **and capture phase**, so the in-match default keys on
 * `capturePhase`: a locked board resumes to the board, a session still in Discovery resumes to
 * Discovery (DISC-1). Halftime resumes to the hub rather than the board, because HT-1 auto-presents
 * it on entry and a kill during the break must not skip it.
 */
export function defaultSurfaceFor(match: MatchRow): string {
  if (match.state === MATCH_STATE.preMatch) return SURFACE.setup;
  if (match.state === MATCH_STATE.halftime) return SURFACE.halftimeHub;
  return match.capturePhase === 'locked' ? SURFACE.board : SURFACE.discovery;
}

/**
 * SES-3's clock half: the match minute a resumed session shows.
 *
 * Exported as its own function so the property SES-3 actually names — *"the clock reconstructed from
 * persisted anchors"* — is testable as a value rather than inferred from a screenshot. There is no
 * hidden state: pass the row you resumed and the current time.
 */
export function resumedMinute(match: MatchRow, now: number): number {
  return deriveMatchMinute(match, match.state, now);
}

/**
 * Records the current surface — §2.5: *"written on surface **entry and exit only** (never on the
 * tap path)"*.
 *
 * That restriction is why this is a plain write rather than a command: it carries no domain
 * decision, has no postcondition, and must not enter the bus's single-flight queue, where it would
 * sit behind a burst of increments and delay the navigation that triggered it. It still goes
 * through `write()`, so it is committed like everything else and cannot open an implicit
 * auto-commit transaction of its own.
 *
 * @throws {StorageError} on a write failure.
 */
export async function recordSurface(
  matchId: string | null,
  surface: string,
  surfaceParams: Record<string, unknown> = {},
  /** Injected like every other clock read in this unit, so `updatedAt` is assertable. */
  now: () => number = () => Date.now(),
): Promise<void> {
  const updatedAt = now();
  await write(
    [TABLE.uiSession],
    () => session.writeUiSession({ matchId, surface, surfaceParams, updatedAt }),
    { label: 'recordSurface' },
  );
}
